import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { WeaponsResponse } from '../api/types';
import { useI18n } from '../i18n';

export interface FilterState {
  kind: string | null;
  weapon: string | null;
  exteriors: string[];
  rarities: string[];
  qualities: string[];
  /** StatTrak™: 'all' | 'yes' | 'no' */
  statTrak: string;
  /** Faixa de preço em reais; null = sem limite naquele lado. */
  minPrice: number | null;
  maxPrice: number | null;
  /** Lado do agente: 'ct' | 'tr'. Vazio = ambos. */
  teams: string[];
  /** Cores da arte (azul, vermelho…). Vazio = todas. */
  colors: string[];
}

interface Props {
  data: WeaponsResponse | null;
  state: FilterState;
  onChange: (next: Partial<FilterState>) => void;
  onClear: () => void;
  search: string;
  onSearch: (value: string) => void;
}

/**
 * Linha de filtros em pílulas que abrem menu, como no buff.163.com.
 *
 * A pílula mostra o que está escolhido (ou "Todos") e o menu só aparece ao
 * clicar — mantém a barra baixa mesmo com muitas opções, sem esconder o estado.
 */
export function FilterBar({ data, state, onChange, onClear, search, onSearch }: Props) {
  const { t, loc } = useI18n();

  const hasFilters =
    state.exteriors.length > 0 ||
    state.rarities.length > 0 ||
    state.qualities.length > 0 ||
    state.weapon !== null ||
    state.statTrak !== 'all' ||
    state.minPrice !== null ||
    state.maxPrice !== null ||
    state.colors.length > 0 ||
    state.teams.length > 0;

  const toggle = (list: string[], value: string) =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  const summary = (selected: string[], labels: Map<string, string>, fallback: string) => {
    if (selected.length === 0) return fallback;
    if (selected.length === 1) return labels.get(selected[0]!) ?? selected[0]!;
    return `${labels.get(selected[0]!) ?? selected[0]!} +${selected.length - 1}`;
  };

  const exteriorLabels = new Map((data?.exteriors ?? []).map((e) => [e.code, loc(e)]));
  const rarityLabels = new Map((data?.rarities ?? []).map((r) => [r.key, loc(r)]));
  const qualityLabels = new Map((data?.qualities ?? []).map((q) => [q.key, loc(q)]));

  return (
    <div className="filterbar">
      {/*
        Os seletores ficam num contêiner próprio para virarem uma faixa de uma
        linha, rolável, no celular: soltos direto na filterbar eles quebravam em
        3 fileiras e a barra do topo comia 73% da tela. Ver styles.css, ≤560px.
      */}
      <div className="filterbar-drops">
      <Dropdown
        label={t('filters.exterior')}
        value={summary(state.exteriors, exteriorLabels, t('filters.all'))}
        active={state.exteriors.length > 0}
      >
        <MenuItem active={state.exteriors.length === 0} onClick={() => onChange({ exteriors: [] })}>
          {t('filters.all')}
        </MenuItem>
        {(data?.exteriors ?? []).map((ext) => (
          <MenuItem
            key={ext.code}
            active={state.exteriors.includes(ext.code)}
            onClick={() => onChange({ exteriors: toggle(state.exteriors, ext.code) })}
            // No Buff cada desgaste vem na sua cor — ajuda a bater o olho.
            color={wearColor(ext.code)}
          >
            {loc(ext)}
          </MenuItem>
        ))}
      </Dropdown>

      <Dropdown
        label={t('filters.rarity')}
        value={summary(state.rarities, rarityLabels, t('filters.allFem'))}
        active={state.rarities.length > 0}
        wide
      >
        <MenuItem active={state.rarities.length === 0} onClick={() => onChange({ rarities: [] })}>
          {t('filters.allFem')}
        </MenuItem>
        {(data?.rarities ?? []).map((rarity) => (
          <MenuItem
            key={rarity.key}
            active={state.rarities.includes(rarity.key)}
            onClick={() => onChange({ rarities: toggle(state.rarities, rarity.key) })}
            color={rarity.color}
          >
            {loc(rarity)}
          </MenuItem>
        ))}
      </Dropdown>

      <Dropdown
        label={t('filters.quality')}
        value={summary(state.qualities, qualityLabels, t('filters.allFem'))}
        active={state.qualities.length > 0}
      >
        <MenuItem active={state.qualities.length === 0} onClick={() => onChange({ qualities: [] })}>
          {t('filters.allFem')}
        </MenuItem>
        {(data?.qualities ?? []).map((quality) => (
          <MenuItem
            key={quality.key}
            active={state.qualities.includes(quality.key)}
            onClick={() => onChange({ qualities: toggle(state.qualities, quality.key) })}
          >
            {loc(quality)}
          </MenuItem>
        ))}
      </Dropdown>

      <Dropdown
        label={t('filters.stattrak')}
        value={
          state.statTrak === 'yes' ? t('filters.yes') : state.statTrak === 'no' ? t('filters.no') : t('filters.all')
        }
        active={state.statTrak !== 'all'}
      >
        <MenuItem active={state.statTrak === 'all'} onClick={() => onChange({ statTrak: 'all' })}>
          {t('filters.all')}
        </MenuItem>
        <MenuItem active={state.statTrak === 'yes'} onClick={() => onChange({ statTrak: 'yes' })} color="#cf6a32">
          {t('filters.yes')}
        </MenuItem>
        <MenuItem active={state.statTrak === 'no'} onClick={() => onChange({ statTrak: 'no' })}>
          {t('filters.no')}
        </MenuItem>
      </Dropdown>

      <PriceDropdown data={data} state={state} onChange={onChange} />
      <ColorDropdown data={data} state={state} onChange={onChange} />

      {hasFilters && (
        <button className="filterbar-clear" onClick={onClear}>
          ✕ {t('filters.clear')}
        </button>
      )}
      </div>

      <div className="filterbar-search">
        <input
          className="input"
          placeholder={t('level2.searchPlaceholder')}
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
      </div>
    </div>
  );
}

/**
 * Filtro de preço: faixas prontas para o caminho rápido e min/máx livre para
 * quem tem um valor exato em mente. Sempre em reais — a API converte para yuan
 * pela taxa da Wise.
 */
/**
 * Filtro de cor da arte. As opções vêm do de-para importado no painel; sem
 * planilha carregada o filtro nem aparece, em vez de ficar um menu vazio.
 */
function ColorDropdown({
  data,
  state,
  onChange,
}: {
  data: WeaponsResponse | null;
  state: FilterState;
  onChange: (next: Partial<FilterState>) => void;
}) {
  const { t, loc } = useI18n();
  const cores = data?.colors ?? [];
  if (cores.length === 0) return null;

  const rotulos = new Map(cores.map((c) => [c.key, loc(c)]));
  const label =
    state.colors.length === 0
      ? t('filters.all')
      : state.colors.length === 1
        ? (rotulos.get(state.colors[0]!) ?? state.colors[0]!)
        : `${rotulos.get(state.colors[0]!) ?? ''} +${state.colors.length - 1}`;

  const alternar = (key: string) =>
    onChange({
      colors: state.colors.includes(key) ? state.colors.filter((k) => k !== key) : [...state.colors, key],
    });

  return (
    <Dropdown label={t('filters.color')} value={label} active={state.colors.length > 0}>
      <MenuItem active={state.colors.length === 0} onClick={() => onChange({ colors: [] })}>
        {t('filters.all')}
      </MenuItem>
      {cores.map((c) => (
        <MenuItem key={c.key} active={state.colors.includes(c.key)} onClick={() => alternar(c.key)}>
          <span className="color-dot" style={{ background: c.hex }} aria-hidden />
          {loc(c)}
          <span className="menu-count">{c.count}</span>
        </MenuItem>
      ))}
    </Dropdown>
  );
}

function PriceDropdown({
  data,
  state,
  onChange,
}: {
  data: WeaponsResponse | null;
  state: FilterState;
  onChange: (next: Partial<FilterState>) => void;
}) {
  const { t } = useI18n();
  const [min, setMin] = useState(state.minPrice?.toString() ?? '');
  const [max, setMax] = useState(state.maxPrice?.toString() ?? '');

  // "Limpar filtros" zera a URL; os campos precisam acompanhar.
  useEffect(() => {
    setMin(state.minPrice?.toString() ?? '');
    setMax(state.maxPrice?.toString() ?? '');
  }, [state.minPrice, state.maxPrice]);

  const brl = (v: number) => `R$ ${v}`;

  const label = (() => {
    if (state.minPrice === null && state.maxPrice === null) return t('filters.priceAny');
    if (state.maxPrice === null) return t('filters.priceAbove', { min: brl(state.minPrice ?? 0) });
    if (state.minPrice === null) return `≤ ${brl(state.maxPrice)}`;
    return `${brl(state.minPrice)} – ${brl(state.maxPrice)}`;
  })();

  const apply = () => {
    const parse = (v: string) => {
      const n = Number(v.replace(',', '.'));
      return v.trim() !== '' && Number.isFinite(n) && n >= 0 ? n : null;
    };
    const a = parse(min);
    const b = parse(max);
    // Se vierem trocados, corrige em vez de devolver lista vazia.
    onChange(a !== null && b !== null && a > b ? { minPrice: b, maxPrice: a } : { minPrice: a, maxPrice: b });
  };

  return (
    <Dropdown label={t('filters.price')} value={label} active={state.minPrice !== null || state.maxPrice !== null} wide>
      <MenuItem
        active={state.minPrice === null && state.maxPrice === null}
        onClick={() => onChange({ minPrice: null, maxPrice: null })}
      >
        {t('filters.priceAny')}
      </MenuItem>
      {(data?.priceRanges ?? []).map((r) => (
        <MenuItem
          key={r.key}
          active={state.minPrice === r.min && state.maxPrice === r.max}
          onClick={() => onChange({ minPrice: r.min, maxPrice: r.max })}
        >
          {r.max === null ? t('filters.priceAbove', { min: brl(r.min) }) : `${brl(r.min)} – ${brl(r.max)}`}
        </MenuItem>
      ))}

      <div className="fdrop-range">
        <input
          className="input"
          inputMode="decimal"
          placeholder={t('filters.priceMin')}
          value={min}
          onChange={(e) => setMin(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && apply()}
        />
        <span>–</span>
        <input
          className="input"
          inputMode="decimal"
          placeholder={t('filters.priceMax')}
          value={max}
          onChange={(e) => setMax(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && apply()}
        />
        <button className="btn small" onClick={apply}>
          {t('filters.priceApply')}
        </button>
      </div>

      {data && data.minPriceBrl > 0 && (
        <div className="fdrop-note">{t('filters.priceFloor', { value: brl(data.minPriceBrl) })}</div>
      )}
    </Dropdown>
  );
}

/** Cores oficiais dos desgastes do CS2. */
function wearColor(code: string): string {
  switch (code) {
    case 'FN':
      return '#4bb14b';
    case 'MW':
      return '#87c542';
    case 'FT':
      return '#e6b83c';
    case 'WW':
      return '#e08a3c';
    case 'BS':
      return '#e05a48';
    default:
      return '#8b97a6';
  }
}

/**
 * Exportados para a aba Kits: os filtros de lá são os mesmos do catálogo, e
 * repetir o visual em outro componente seria o jeito certo de eles divergirem.
 */
export function Dropdown({
  label,
  value,
  active,
  children,
  wide,
}: {
  label: string;
  value: string;
  active: boolean;
  children: ReactNode;
  wide?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="fdrop" ref={ref}>
      <button
        className={`fdrop-trigger${active ? ' active' : ''}${open ? ' open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="fdrop-label">{label}</span>
        <span className="fdrop-value">{value}</span>
        <span className="fdrop-caret" aria-hidden>
          ▾
        </span>
      </button>
      {open && <div className={`fdrop-menu${wide ? ' wide' : ''}`}>{children}</div>}
    </div>
  );
}

export function MenuItem({
  active,
  onClick,
  children,
  color,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  color?: string;
}) {
  return (
    <button className={`fdrop-item${active ? ' active' : ''}`} onClick={onClick} style={color ? { color } : undefined}>
      <span className="fdrop-check" aria-hidden>
        {active ? '✓' : ''}
      </span>
      {children}
    </button>
  );
}
